import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

export interface IpifyResponse {
  ip?: string;
}

export interface IpApiResponse {
  ip?: string;
  city?: string;
  region?: string;
  country_name?: string;
}

@Injectable({ providedIn: 'root' })
export class ClientMetadataService {
  resolveMetadata(): Observable<void> {
    const savedIp = localStorage.getItem('sl_client_ip');
    if (savedIp && !savedIp.includes(':')) {
      return of(undefined);
    }

    // Use reactive from(fetch(...)) with RxJS timeout & fallback protection
    // to avoid coupling Angular HttpClient interceptors during application startup & unit tests.
    return from(fetch('https://api.ipify.org?format=json')).pipe(
      timeout(3000),
      switchMap(res => {
        if (!res.ok) throw new Error('ipify request failed');
        return from(res.json() as Promise<IpifyResponse>);
      }),
      switchMap(ipData => {
        if (!ipData?.ip) {
          throw new Error('No IP returned from ipify');
        }
        const ipv4 = ipData.ip;
        return from(fetch(`https://ipapi.co/${ipv4}/json/`)).pipe(
          timeout(3000),
          switchMap(res => {
            if (!res.ok) throw new Error('ipapi request failed');
            return from(res.json() as Promise<IpApiResponse>);
          }),
          map(locData => {
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty string from the API should also fall back to default
            const locStr = `${locData?.city || 'Delhi'}, ${locData?.region ? locData.region + ', ' : ''}${locData?.country_name || 'India'}`;
            localStorage.setItem('sl_client_ip', ipv4);
            localStorage.setItem('sl_client_location', locStr);
          }),
          catchError(err => {
            console.warn('Failed to resolve location from ipapi.co, using defaults', err);
            localStorage.setItem('sl_client_ip', ipv4);
            localStorage.setItem('sl_client_location', 'Delhi, India');
            return of(undefined);
          }),
        );
      }),
      catchError(err => {
        console.warn(
          'Failed to fetch client IPv4 via ipify, falling back to ipapi.co directly...',
          err,
        );
        return from(fetch('https://ipapi.co/json/')).pipe(
          timeout(3000),
          switchMap(res => from(res.json() as Promise<IpApiResponse>)),
          map(data => {
            if (data?.ip) {
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty string from the API should also fall back to default
              const locStr = `${data.city || 'Delhi'}, ${data.region ? data.region + ', ' : ''}${data.country_name || 'India'}`;
              localStorage.setItem('sl_client_ip', data.ip);
              localStorage.setItem('sl_client_location', locStr);
            }
          }),
          catchError(() => of(undefined)),
        );
      }),
    );
  }
}
